/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Icon, Spinner, Text, Title } from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { Page } from '@nimbus-ds/patterns';
import { Country } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import { NE_PRINT_LABEL_SOURCE } from 'domains/NuvemEnvioCommons/constants';
import {
  trackingNuvemEnvioPrintLabelsError,
  trackingNuvemEnvioPrintLabelsSuccess,
  trackingNuvemEnvioPrintLabelsWaitingPayment,
  trackingNuvemEnvioPrintLabelsWaitingPaymentButton,
} from 'domains/NuvemEnvioCommons/tracking';
import { ORDERS_SECTION_ROUTES } from 'domains/Orders/Orders/ordersSectionRoutes';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import useNuvemEnvioTranslations from '../../../hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import useCreateNuvemEnvioLabels from '../../hooks/useCreateNuvemEnvioLabels/useCreateNuvemEnvioLabels';
import { useGoBackFromLabelPrinting } from '../../hooks/useGoBackFromLabelPrinting';
import useGoToPrintNuvemEnvioLabelsPage from '../../hooks/useGoToPrintNuvemEnvioLabels/useGoToPrintNuvemEnvioLabels';
import { useLabelPrintingQuery } from '../../hooks/useLabelPrintingQuery';
import { usePolling } from '../../hooks/usePolling/usePolling';
import { usePrintNuvemEnvioLabelToast } from '../../hooks/usePrintNuvemEnvioLabelToast';
import useSimulateNuvemEnvioLabels from '../../hooks/useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';
import ordersService from '../../ordersService/ordersService';
import { hasOnlyErrorsOnPrintLabel, openInNewTab } from '../../utils';
import {
  NE_POOLING_LABELS_INTERVAL,
  NE_POOLING_LABELS_TIMEOUT,
} from '../../utils/pooling';

function OrdersLabelGenerateStatusPage(): JSX.Element {
  const { id: storeId } = useStoreInfo();
  const t = useNuvemEnvioTranslations();
  const {
    isPostPaid,
    status: nuvemEnvioConfigsStatus,
    hasPrintLabelDocumentsPageEnabled,
    shouldShowAsyncProcessing,
  } = useGetNuvemEnvioConfigs();
  const { createNuvemEnvioLabels, resetNuvemEnvioLabels } =
    useCreateNuvemEnvioLabels();
  const { orderId, source, fulfillmentOrderIds } = useLabelPrintingQuery({
    fulfillmentOrderIdsKey: 'ids',
  });
  const country = useGetCountry();
  const { goToPrintNuvemEnvioLabelDocumentsPage } =
    useGoToPrintNuvemEnvioLabelsPage();

  const { goTo } = useNavegate();
  const { goBack } = useGoBackFromLabelPrinting({ orderId, source });
  const { addSuccessToast, addErrorToast } = usePrintNuvemEnvioLabelToast();
  const [shouldShowLoading, setShouldShowLoading] = useState(true);

  const {
    data: simulateNuvemEnvioLabelsData,
    status: simulateNuvemEnvioLabelsStatus,
  } = useSimulateNuvemEnvioLabels();

  const shouldStartPolling =
    nuvemEnvioConfigsStatus.isSuccess &&
    !isPostPaid &&
    (simulateNuvemEnvioLabelsStatus.isIdle ||
      !!simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge);

  const shouldCreateLabels =
    nuvemEnvioConfigsStatus.isSuccess &&
    fulfillmentOrderIds.length &&
    (isPostPaid ||
      (!simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge &&
        simulateNuvemEnvioLabelsStatus.isSuccess));

  const shouldCreateFilesOnDocumentsPage =
    hasPrintLabelDocumentsPageEnabled &&
    source === NE_PRINT_LABEL_SOURCE.ORDER_DETAILS &&
    fulfillmentOrderIds.length === 1;

  const createLabels = useCallback(async () => {
    try {
      const response = await createNuvemEnvioLabels({
        createFile: shouldCreateFilesOnDocumentsPage
          ? { label: false, contentDeclaration: false }
          : {
              label: true,
              contentDeclaration: country === Country.BR ? true : false,
            },
        fulfillmentOrderIds,
      });

      if (hasOnlyErrorsOnPrintLabel(response)) {
        throw new Error('Unexpected error creating labels');
      }

      trackingNuvemEnvioPrintLabelsSuccess(storeId, source);
      addSuccessToast();

      if (shouldCreateFilesOnDocumentsPage) {
        goToPrintNuvemEnvioLabelDocumentsPage({
          fulfillmentOrderIds: fulfillmentOrderIds,
          source,
          orderId,
          asyncProcess: shouldShowAsyncProcessing,
        });
      } else {
        for (const url of response.labelUrls) openInNewTab(url);
        for (const url of response.contentDeclarationUrls) openInNewTab(url);
        goBack();
      }
    } catch (error) {
      trackingNuvemEnvioPrintLabelsError(storeId, source);
      addErrorToast();
      goBack();
    }
  }, [
    createNuvemEnvioLabels,
    shouldCreateFilesOnDocumentsPage,
    country,
    fulfillmentOrderIds,
    addSuccessToast,
    addErrorToast,
    goBack,
    source,
    storeId,
    orderId,
    goToPrintNuvemEnvioLabelDocumentsPage,
    shouldShowAsyncProcessing,
  ]);

  usePolling(
    shouldStartPolling,
    async () => {
      const response = await ordersService.simulateNuvemEnvioLabels({
        fulfillmentOrderIds,
      });

      return !response?.balanceInfo?.shouldRecharge;
    },
    {
      interval: NE_POOLING_LABELS_INTERVAL,
      timeout: NE_POOLING_LABELS_TIMEOUT,
      onSuccess: createLabels,
      onTimeout: () => {
        trackingNuvemEnvioPrintLabelsWaitingPayment(storeId);
        setShouldShowLoading(false);
      },
    },
  );

  useEffect(() => {
    if (shouldCreateLabels) createLabels();

    return () => {
      resetNuvemEnvioLabels();
    };
  }, [createLabels, shouldCreateLabels, resetNuvemEnvioLabels]);

  return (
    <Page>
      <Page.Body height="90vh" display="flex" alignItems="center">
        <Box
          m="auto"
          width={{
            md: '100%',
            xs: '340px',
          }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="4"
        >
          {shouldShowLoading ? (
            <Spinner size="large" color="neutral-textLow" />
          ) : (
            <Icon source={<ExclamationCircleIcon size="large" />} />
          )}
          <Title as="h4">
            {t(
              shouldShowLoading
                ? 'printNuvemEnvioLabelsStatus.processing.title'
                : 'creditRechargeNuvemEnvioLabels.sidebar.title',
            )}
          </Title>
          <Box width="70%">
            <Text textAlign="center">
              {t(
                shouldShowLoading
                  ? 'printNuvemEnvioLabelsStatus.processing.description'
                  : 'creditRechargeNuvemEnvioLabels.sidebar.text',
              )}
            </Text>
          </Box>
          {!shouldShowLoading && (
            <Button
              onClick={() => {
                trackingNuvemEnvioPrintLabelsWaitingPaymentButton(storeId);
                goTo(ORDERS_SECTION_ROUTES.orderList);
              }}
              appearance="primary"
            >
              {t('creditRechargeNuvemEnvioLabels.sidebar.button')}
            </Button>
          )}
        </Box>
      </Page.Body>
    </Page>
  );
}

export default OrdersLabelGenerateStatusPage;
